import { relativeTaskRange } from "./preliminary-schedule";

// Draw directly into a single PDF page; browser/printer paper settings do not apply.
export function createSinglePageSchedulePdf(Pdf, { project, phases, tasks, days, title, logo }) {
  const pdf = new Pdf({ orientation: "landscape", unit: "mm", format: "a4" });
  const width = pdf.internal.pageSize.getWidth();
  const height = pdf.internal.pageSize.getHeight();
  const margin = 10, tableWidth = width - margin * 2;
  const graphX = margin + 102, graphWidth = tableWidth - 102;
  const relative = Boolean(days[0]?.relativeDay);
  const text = (value, x, y, size = 7, color = "#243149", options = {}) => {
    pdf.setFontSize(size); pdf.setTextColor(color); pdf.text(String(value), x, y, options);
  };
  pdf.setFillColor("#15191a"); pdf.rect(margin, 9, tableWidth, 27, "F");
  if (logo) pdf.addImage(logo, "PNG", margin + 4, 16, 52, 12, undefined, "SLOW");
  else text("ABOUTPLAN", margin + 5, 24, 17, "#ffffff");
  text(title, width - margin - 4, 16, 11, "#ffffff", { align: "right" });
  text(`${project.projectNumber || ""} · ${project.name}`, width - margin - 4, 22, 8, "#ffffff", { align: "right" });
  text(`${project.client || ""} / ${project.unit || ""}`, width - margin - 4, 28, 8, "#ffffff", { align: "right" });
  pdf.setDrawColor("#bba16c"); pdf.line(margin, 36, width - margin, 36);
  text(relative ? "Cronograma preliminar" : "Cronograma planejado", margin, 42, 9);
  const datedTasks = tasks.flatMap(task => [task.startDate, task.endDate]).filter(Boolean).sort();
  const startDate = project.startDate || datedTasks[0];
  const endDate = project.endDate || datedTasks.at(-1);
  const formatDate = value => value ? `${value.slice(8, 10)}/${value.slice(5, 7)}/${value.slice(0, 4)}` : "A definir";
  const duration = relative ? days.length : startDate && endDate
    ? Math.round((Date.parse(`${endDate}T12:00:00Z`) - Date.parse(`${startDate}T12:00:00Z`)) / 86400000) + 1
    : days.length;
  const summary = [
    { x: 107, label: "DATA DE INÍCIO", value: formatDate(startDate), icon: "calendar" },
    { x: 159, label: "DATA DE TÉRMINO", value: formatDate(endDate), icon: "calendar" },
    { x: 212, label: "DURAÇÃO DA OBRA EM DIAS CORRIDOS", value: `${duration} dias`, icon: "clock" },
  ];
  summary.forEach(({ x, label, value, icon }) => {
    pdf.setDrawColor("#52647c"); pdf.setLineWidth(.25);
    if (icon === "calendar") {
      pdf.roundedRect(x, 38.4, 3.2, 3.2, .35, .35);
      pdf.line(x, 39.4, x + 3.2, 39.4);
      pdf.line(x + .8, 37.9, x + .8, 38.9);
      pdf.line(x + 2.4, 37.9, x + 2.4, 38.9);
    } else {
      pdf.circle(x + 1.6, 40, 1.6);
      pdf.line(x + 1.6, 40, x + 1.6, 39);
      pdf.line(x + 1.6, 40, x + 2.4, 40.5);
    }
    text(label, x + 4.3, 40.7, 5.2, "#52647c");
    text(value, x + 4.3, 45, 7);
  });
  const rows = [];
  for (const phase of phases) {
    const members = tasks.filter(task => task.phaseId === phase.id);
    if (members.length) rows.push({ phase }, ...members.map(task => ({ task, color: phase.color })));
  }
  rows.push(...tasks.filter(task => !phases.some(phase => phase.id === task.phaseId)).map(task => ({ task, color: "#9d8554" })));
  const rowHeight = Math.min(8.5, (height - 67) / Math.max(1, rows.length));
  const dayWidth = graphWidth / Math.max(1, days.length);
  let y = 48;
  pdf.setFillColor("#f0f4fa"); pdf.rect(margin, y, tableWidth, 9, "F");
  text("ATIVIDADE", margin + 2, y + 5, 7);
  text("DURAÇÃO", margin + 66, y + 5, 7);
  text(relative ? "DIAS PREVISTOS" : "PERÍODO", margin + 81, y + 5, 7);
  days.forEach((day, i) => {
    const x = graphX + i * dayWidth;
    const label = String(relative ? day.relativeDay : day.number);
    const baseSize = relative ? 5 : 4;
    pdf.setFontSize(baseSize);
    const labelSize = Math.min(baseSize, baseSize * dayWidth * .85 / Math.max(.1, pdf.getTextWidth(label)));
    text(label, x + dayWidth / 2, y + 5, labelSize, "#52647c", { align: "center" });
  });
  y += 9;
  rows.forEach(({ phase, task, color }) => {
    if (phase) {
      pdf.setFillColor("#f1f0ec"); pdf.rect(margin, y, tableWidth, rowHeight, "F");
      text(phase.name, margin + 2, y + rowHeight * .62, 7);
    } else {
      pdf.setFontSize(7);
      const maxNameWidth = 62;
      let name = task.name;
      while (name.length && pdf.getTextWidth(name) > maxNameWidth) name = name.slice(0, -1);
      if (name !== task.name) {
        while (name.length && pdf.getTextWidth(`${name.trimEnd()}…`) > maxNameWidth) name = name.slice(0, -1);
        name = `${name.trimEnd()}…`;
      }
      text(name, margin + 2, y + rowHeight * .68, 7);
      text(`${task.durationDays} dias`, margin + 67, y + rowHeight * .6, 7);
      const range = relativeTaskRange(task);
      const period = relative && range ? `${range.first}–${range.last}` : task.startDate && task.endDate ? `${task.startDate.slice(8)}/${task.startDate.slice(5,7)}–${task.endDate.slice(8)}/${task.endDate.slice(5,7)}` : "A definir";
      text(period, margin + 84, y + rowHeight * .6, 7);
      days.forEach((day, i) => {
        if (day.weekend && !relative) { pdf.setFillColor("#f5f2ea"); pdf.rect(graphX + i * dayWidth, y, dayWidth, rowHeight, "F"); }
        pdf.setDrawColor("#e3e8ef"); pdf.line(graphX + i * dayWidth, y, graphX + i * dayWidth, y + rowHeight);
      });
      const included = days.flatMap((day, i) => (relative ? range && day.relativeDay >= range.first && day.relativeDay <= range.last : task.startDate && task.endDate && day.iso >= task.startDate && day.iso <= task.endDate) ? [i] : []);
      if (included.length) {
        pdf.setFillColor(color || "#9d8554");
        pdf.rect(graphX + included[0] * dayWidth, y + rowHeight * .3, included.length * dayWidth, rowHeight * .4, "F");
      }
    }
    pdf.setDrawColor("#e3e8ef"); pdf.line(margin, y + rowHeight, width - margin, y + rowHeight);
    y += rowHeight;
  });
  text("ABOUTPLAN · Cronograma físico", margin, height - 7, 7);
  text("1 / 1 · A4 horizontal", width - margin, height - 7, 7, "#52647c", { align: "right" });
  return pdf;
}

// Each PDF page owns both its activity columns and its slice of the timeline.
export function createPagedSchedulePdf(Pdf, { project, phases, tasks, days, title, logo }) {
  const pdf = new Pdf({ orientation: "landscape", unit: "mm", format: "a3" });
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 12;
  const contentWidth = pageWidth - margin * 2;
  const activityWidth = 112, ownerWidth = 43, durationWidth = 21, progressWidth = 20;
  const timelineX = margin + activityWidth + ownerWidth + durationWidth + progressWidth;
  const timelineWidth = contentWidth - (timelineX - margin);
  const rowHeight = 10;
  const rowsPerPage = Math.floor((pageHeight - 83) / rowHeight);
  const relative = Boolean(days[0]?.relativeDay);
  const dateLabel = day => relative ? `Dia ${day.relativeDay}` : `${day.iso.slice(8, 10)}/${day.iso.slice(5, 7)}`;
  const fullDateLabel = day => relative ? `Dia ${day.relativeDay}` : `${day.iso.slice(8, 10)}/${day.iso.slice(5, 7)}/${day.iso.slice(0, 4)}`;
  const text = (value, x, y, size = 8, color = "#243149", options = {}) => {
    pdf.setFont("helvetica", "normal"); pdf.setFontSize(size); pdf.setTextColor(color);
    pdf.text(String(value), x, y, options);
  };
  const fit = (value, width, size = 8) => {
    pdf.setFontSize(size);
    const lines = pdf.splitTextToSize(String(value || "—"), width);
    return lines.slice(0, 2).map((line, index) => index === 1 && lines.length > 2 ? `${line.replace(/\s+$/, "")}…` : line);
  };
  const bands = [];
  for (let offset = 0; offset < days.length; offset += 21) {
    const band = days.slice(offset, offset + 21);
    const bandTasks = tasks.filter(task => {
      const range = relativeTaskRange(task);
      return relative
        ? !range ? offset === 0 : range.first <= band.at(-1).relativeDay && range.last >= band[0].relativeDay
        : !task.startDate || !task.endDate ? offset === 0 : task.startDate <= band.at(-1).iso && task.endDate >= band[0].iso;
    });
    const rows = [];
    phases.forEach(phase => {
      const members = bandTasks.filter(task => task.phaseId === phase.id);
      if (members.length) rows.push({ phase }, ...members.map(task => ({ task, color: phase.color })));
    });
    rows.push(...bandTasks.filter(task => !phases.some(phase => phase.id === task.phaseId)).map(task => ({ task, color: "#9d8554" })));
    if (!rows.length) rows.push({ empty: true });
    let cursor = 0;
    while (cursor < rows.length) {
      const part = rows.slice(cursor, cursor + rowsPerPage);
      if (part.at(-1)?.phase && cursor + rowsPerPage < rows.length) part.pop();
      bands.push({ days: band, rows: part, part: Math.floor(cursor / rowsPerPage) + 1 });
      cursor += part.length;
    }
  }
  bands.forEach(({ days: band, rows }, pageIndex) => {
    if (pageIndex) pdf.addPage();
    pdf.setFillColor("#15191a"); pdf.rect(margin, 10, contentWidth, 28, "F");
    if (logo) pdf.addImage(logo, "PNG", margin + 5, 18, 58, 13, undefined, "SLOW");
    else text("ABOUTPLAN", margin + 5, 28, 18, "#ffffff");
    text(title, pageWidth - margin - 5, 19, 13, "#ffffff", { align: "right" });
    text(`${project.projectNumber || ""} · ${project.name || ""}`, pageWidth - margin - 5, 26, 9, "#ffffff", { align: "right" });
    text(`${project.client || project.clientName || ""} / ${project.unit || project.unitName || ""}`, pageWidth - margin - 5, 33, 9, "#ffffff", { align: "right" });
    pdf.setDrawColor("#bba16c"); pdf.line(margin, 38, pageWidth - margin, 38);
    pdf.setFillColor("#f3f6fa"); pdf.rect(margin, 42, contentWidth, 14, "F");
    text(`PERÍODO DESTA PÁGINA  ${fullDateLabel(band[0])} a ${fullDateLabel(band.at(-1))}`, margin + 4, 48, 8);
    text(`PLANEJAMENTO COMPLETO  ${fullDateLabel(days[0])} a ${fullDateLabel(days.at(-1))}`, margin + 4, 53, 8);
    text(`PÁGINA ${pageIndex + 1} DE ${bands.length}`, pageWidth - margin - 4, 51, 8, "#52647c", { align: "right" });
    let y = 62;
    pdf.setFillColor("#f1f0ec"); pdf.rect(margin, y, contentWidth, 11, "F");
    text("FASE / ATIVIDADE", margin + 3, y + 7, 8);
    text("RESPONSÁVEL", margin + activityWidth + 2, y + 7, 8);
    text("DURAÇÃO", margin + activityWidth + ownerWidth + 2, y + 7, 8);
    text("AVANÇO", timelineX - progressWidth + 2, y + 7, 8);
    const dayWidth = timelineWidth / band.length;
    band.forEach((day, i) => text(dateLabel(day), timelineX + (i + .5) * dayWidth, y + 7, 7, "#52647c", { align: "center" }));
    y += 11;
    rows.forEach(({ phase, task, color, empty }) => {
      if (phase) {
        pdf.setFillColor("#f1f0ec"); pdf.rect(margin, y, contentWidth, rowHeight, "F");
        pdf.setFillColor(color || "#9d8554"); pdf.rect(margin + 2, y + 3, 3, 3, "F");
        text(phase.name, margin + 8, y + 6.7, 9);
      } else if (empty) {
        text("Nenhuma atividade prevista neste período", margin + 3, y + 6.5, 8);
      } else {
        const range = relativeTaskRange(task);
        const nameLines = fit(task.name, activityWidth - 6, 8.5);
        nameLines.forEach((line, i) => text(line, margin + 3, y + (nameLines.length === 1 ? 6.5 : 4.5 + i * 4), 8.5));
        fit(task.assignedTo || "Não atribuído", ownerWidth - 4, 7.5).forEach((line, i) => text(line, margin + activityWidth + 2, y + 4.5 + i * 4, 7.5));
        text(`${task.durationDays || "—"} dias`, margin + activityWidth + ownerWidth + 2, y + 6.5, 8);
        text(`${Number(task.progress || 0)}%`, timelineX - progressWidth + 2, y + 6.5, 8);
        band.forEach((day, i) => {
          const x = timelineX + i * dayWidth;
          if (day.weekend && !relative) { pdf.setFillColor("#f5f2ea"); pdf.rect(x, y, dayWidth, rowHeight, "F"); }
          pdf.setDrawColor("#e3e8ef"); pdf.line(x, y, x, y + rowHeight);
        });
        const included = band.flatMap((day, i) => (relative
          ? range && day.relativeDay >= range.first && day.relativeDay <= range.last
          : task.startDate && task.endDate && day.iso >= task.startDate && day.iso <= task.endDate) ? [i] : []);
        if (included.length) {
          pdf.setFillColor(color || "#9d8554");
          pdf.rect(timelineX + included[0] * dayWidth + .3, y + 3, included.length * dayWidth - .6, 4, "F");
        }
      }
      pdf.setDrawColor("#e3e8ef"); pdf.line(margin, y + rowHeight, pageWidth - margin, y + rowHeight);
      y += rowHeight;
    });
    text("ABOUTPLAN · Cronograma físico", margin, pageHeight - 8, 8, "#52647c");
    text(`${pageIndex + 1} / ${bands.length} · A3 horizontal`, pageWidth - margin, pageHeight - 8, 8, "#52647c", { align: "right" });
  });
  return pdf;
}
